/**
 * Draws an ECharts option to a PNG picture, off screen, for a report. Browser
 * only (it needs a canvas); the option comes from the same builders as the
 * on-screen chart, with `interactive: false` and dark text for paper.
 */
import { loadEcharts } from './loadEcharts';

export const REPORT_IMAGE_WIDTH = 1200;
export const REPORT_IMAGE_HEIGHT = 560;
const FINISH_TIMEOUT_MS = 5000;

/**
 * @param {object} option - an ECharts option
 * @param {{ width?: number, height?: number, pixelRatio?: number }} [size]
 * @returns {Promise<{ dataUrl: string, width: number, height: number }>} the picture and its
 *   size in layout pixels (the picture itself is `pixelRatio` times larger)
 */
export async function renderOptionToPng(option, {
    width = REPORT_IMAGE_WIDTH, height = REPORT_IMAGE_HEIGHT, pixelRatio = 2,
} = {}) {
    const echarts = await loadEcharts();
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;height:${height}px;`;
    document.body.appendChild(host);
    const chart = echarts.init(host, null, { renderer: 'canvas', width, height });
    try {
        // A large chart (a heatmap of thousands of cells) paints over several
        // frames: wait until ECharts says it has finished, or the picture can be
        // partial and the late paint fails on the disposed chart.
        await new Promise((resolve) => {
            const timer = setTimeout(resolve, FINISH_TIMEOUT_MS);
            chart.on('finished', () => {
                clearTimeout(timer);
                resolve();
            });
            chart.setOption({ ...option, animation: false });
        });
        const dataUrl = chart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#ffffff' });
        return { dataUrl, width, height };
    } finally {
        chart.dispose();
        document.body.removeChild(host);
    }
}
